import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Autocomplete, Chip, Stack, TextField, Typography } from '@mui/material';

import { useSearchProducts } from '../../hooks';
import type { Product } from '../../model/types';

export interface ProductMultiPickerProps {
  value: Product[];
  onChange: (value: Product[]) => void;
  disabled?: boolean;
  label?: string;
}

/**
 * Explicit product multi-select (design spec §8.8 step ①/§8.9) shared by the bulk-repricing-batch
 * wizard and the markdown-plan create dialog — no file upload, no "whole category": `BatchSource
 * .FILE`/`.RULE` selection are unimplemented backend stubs (research R12), so both surfaces stay
 * with an explicit `productIds[]`. Mirrors `PriceListDetailPage`'s own "Add product" Autocomplete
 * (this feature's established pattern — no debounce, unlike `features/promotions`' own picker).
 */
export function ProductMultiPicker({ value, onChange, disabled, label }: ProductMultiPickerProps) {
  const { t } = useTranslation('catalog');
  const [query, setQuery] = useState('');
  const searchQuery = useSearchProducts({ query }, { enabled: query.trim() !== '' });
  const options = searchQuery.data?.content ?? [];

  return (
    <Stack spacing={0.5}>
      <Autocomplete<Product, true, false, false>
        multiple
        fullWidth
        disabled={disabled}
        value={value}
        options={options}
        loading={searchQuery.isFetching}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        getOptionLabel={(option) => option.name}
        inputValue={query}
        onInputChange={(_event, next, reason) => {
          if (reason === 'input') setQuery(next);
          if (reason === 'clear') setQuery('');
        }}
        onChange={(_event, next) => onChange(next)}
        renderTags={(tags, getTagProps) =>
          tags.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip size="small" label={option.name} key={key} {...tagProps} />;
          })
        }
        renderInput={(params) => (
          <TextField {...params} label={label ?? t('bulkBatchesPanel.productsLabel')} />
        )}
      />
      <Typography variant="caption" color="text.secondary">
        {t('bulkBatchesPanel.selectedCount', { count: value.length })}
      </Typography>
    </Stack>
  );
}
